import {
  CssBaseline,
  StyledEngineProvider,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { csCZ } from "@mui/material/locale";
import { ThemeProvider as StylesThemeProvider } from "@mui/styles";
import { setUserId } from "firebase/analytics";
import { createBrowserNavigation } from "navi";
import { useObservableState, useSubscription } from "observable-hooks";
import React, { Suspense, useEffect, useMemo } from "react";
import { Router, View } from "react-navi";
import { authState, user } from "rxfire/auth";
import { map } from "rxjs/operators";

import InstallPrompt from "./components/pwa/InstallPrompt";
import Snackbar from "./components/snackbar";
import { NaviContextProvider } from "./contexts/navi";
import { RolesProvider } from "./contexts/roles";
import { analytics, auth } from "./firebase";
import { rollbar } from "./lib/rollbar";
import routes from "./routes";
import { Loading } from "./screens/loading";
import { clearStores } from "./stores";

const theme = createTheme(
  {
    typography: {
      fontFamily: "'Poppins', sans-serif",
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 400,
      fontWeightBold: 600,
      body1: {
        fontWeight: 400,
      },
      body2: {
        fontWeight: 400,
      },
      subtitle1: {
        fontWeight: 400,
        lineHeight: 1.75,
      },
      subtitle2: {
        fontWeight: 400,
        lineHeight: 1.75,
      },
      button: {
        fontWeight: 500,
      },
      caption: {
        fontWeight: 400,
      },
      overline: {
        fontWeight: 400,
      },
    },
    palette: {
      primary: {
        main: "#456ed6",
        light: "#7e9cff",
        dark: "#0044a4",
      },
      secondary: {
        main: "#f44336",
      },
      success: {
        main: "#89D042",
      },
      text: {
        primary: "#333333",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 400,
          },
          strong: {
            fontWeight: 600,
          },
          b: {
            fontWeight: 600,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          input: {
            backgroundColor: "#fff",
            borderRadius: "5px",
          },
          multiline: {
            backgroundColor: "#fff",
          },
          adornedEnd: {
            backgroundColor: "#fff",
          },
          adornedStart: {
            backgroundColor: "#fff",
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          h3: {
            fontSize: "1.2em",
            fontWeight: 500,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontWeight: 400,
          },
          head: {
            fontWeight: 500,
          },
        },
      },
    },
  },
  csCZ
);

export default function App() {
  const [[firebaseUser, userLoaded]] = useObservableState(
    () => user(auth).pipe(map((value) => [value, true])),
    [undefined, false]
  );

  useSubscription(authState(auth), (state) => {
    if (!state) {
      clearStores();
    }
  });

  useEffect(() => {
    if (!userLoaded || !firebaseUser) {
      return;
    }
    setUserId(analytics, firebaseUser.uid);
    rollbar.configure({
      payload: {
        person: {
          id: firebaseUser.uid,
          username: firebaseUser.phoneNumber,
          email: firebaseUser.email,
        },
      },
    });
    window.$crisp?.push(["set", "user:email", [firebaseUser.email]]);
  }, [firebaseUser, userLoaded]);

  const context = {
    user: firebaseUser,
    userLoaded,
  };

  let navigation = useMemo(
    () =>
      createBrowserNavigation({
        routes,
        trailingSlash: "add",
      }),
    []
  );

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <StylesThemeProvider theme={theme}>
          <NaviContextProvider context={context}>
            <RolesProvider>
              <Router context={context} navigation={navigation}>
                <Suspense fallback={<Loading />}>
                  <CssBaseline />
                  <View />
                  <Snackbar />
                  <InstallPrompt />
                </Suspense>
              </Router>
            </RolesProvider>
          </NaviContextProvider>
        </StylesThemeProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
